import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Users, DollarSign, FileCheck, AlertCircle, Calendar, TrendingUp, Shield, UserCheck, Clock, MessageSquare, HandHeart, X, Send, Megaphone, Pin, AlertTriangle, ChevronRight, ChevronDown, ClipboardList, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminTasksWidget from '../components/AdminTasksWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [rosterStats, setRosterStats] = useState(null);
  const [scheduleStats, setScheduleStats] = useState(null);
  const [communityStats, setCommunityStats] = useState(null);
  const [volunteerStats, setVolunteerStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Announcement Modal State
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  // Public Page group state — collapses Manage Gallery / Player Spotlights /
  // Sponsors / Registration Config under one "Manage Public Page" accordion
  // to shorten the Quick Actions card.
  const [showPublicPageMenu, setShowPublicPageMenu] = useState(false);
  const [teams, setTeams] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    body: '',
    audience_type: 'all_users',
    team_id: '',
    division_id: '',
    priority: 'normal',
    is_critical: false,
    is_pinned: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [announcementSuccess, setAnnouncementSuccess] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchTeamsAndDivisions();
  }, []);

  const fetchTeamsAndDivisions = async () => {
    try {
      const [teamsRes, divisionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/teams`, { withCredentials: true }),
        axios.get(`${API_URL}/api/divisions`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setTeams(teamsRes.data || []);
      setDivisions(divisionsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch teams/divisions:', error);
    }
  };

  const fetchDashboard = async () => {
    try {
      const [dashRes, rosterRes, scheduleRes, commRes, volRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/admin`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/roster-stats`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/schedule/stats/admin`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/community/stats`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/volunteer/stats`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setDashboard(dashRes.data);
      setRosterStats(rosterRes.data);
      setScheduleStats(scheduleRes.data);
      setCommunityStats(commRes.data);
      setVolunteerStats(volRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendAnnouncement = async () => {
    if (!announcementForm.title.trim() || !announcementForm.body.trim()) {
      alert('Please fill in both title and message');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        title: announcementForm.title,
        body: announcementForm.body,
        audience_type: announcementForm.audience_type,
        priority: announcementForm.priority,
        is_critical: announcementForm.is_critical,
        is_pinned: announcementForm.is_pinned
      };
      
      if (announcementForm.audience_type === 'team' && announcementForm.team_id) {
        payload.team_id = announcementForm.team_id;
        payload.comm_type = 'team_announcement';
      } else if (announcementForm.audience_type === 'division' && announcementForm.division_id) {
        payload.division_id = announcementForm.division_id;
        payload.comm_type = 'division_announcement';
      } else {
        payload.comm_type = 'org_announcement';
      }
      
      await axios.post(`${API_URL}/api/announcements`, payload, { withCredentials: true });
      
      setAnnouncementSuccess(true);
      setTimeout(() => {
        setShowAnnouncementModal(false);
        setAnnouncementSuccess(false);
        setAnnouncementForm({
          title: '',
          body: '',
          audience_type: 'all_users',
          team_id: '',
          division_id: '',
          priority: 'normal',
          is_critical: false,
          is_pinned: false
        });
      }, 1500);
    } catch (error) {
      console.error('Failed to send announcement:', error);
      alert(error.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setSubmitting(false);
    }
  };

  const openAnnouncementModal = () => {
    setShowAnnouncementModal(true);
    setAnnouncementSuccess(false);
  };

  // Announcement Modal JSX is rendered inline at the bottom of this component
  // (intentionally NOT a nested function component — defining a component
  // inside the render function causes React to remount it on every parent
  // re-render, which wipes input state/focus after a single keystroke).
  const renderAnnouncementModal = () => {
    if (!showAnnouncementModal) return null;

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-200">
          {/* Header */}
          <div className="sticky top-0 bg-navy border-b border-navy p-4 flex items-center justify-between rounded-t-xl">
            <div className="flex items-center space-x-2">
              <Megaphone className="w-5 h-5 text-gold" />
              <h2 className="font-display text-lg font-black text-white uppercase tracking-tight">Send Announcement</h2>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setShowAnnouncementModal(false)} className="text-white hover:bg-white/10">
              <X className="w-5 h-5" />
            </Button>
          </div>

          {announcementSuccess ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-display text-xl font-black text-green-600 uppercase mb-2">Sent!</h3>
              <p className="text-gray-600">Your announcement has been delivered.</p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Title *</label>
                <Input
                  value={announcementForm.title}
                  onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                  placeholder="e.g., Practice Schedule Change"
                  data-testid="announcement-title"
                  className="border-gray-200 focus:border-light-blue"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Message *</label>
                <Textarea
                  value={announcementForm.body}
                  onChange={(e) => setAnnouncementForm({...announcementForm, body: e.target.value})}
                  placeholder="Write your announcement..."
                  rows={4}
                  data-testid="announcement-body"
                  className="border-gray-200 focus:border-light-blue"
                />
              </div>

              {/* Audience */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Send To</label>
                <select
                  value={announcementForm.audience_type}
                  onChange={(e) => setAnnouncementForm({...announcementForm, audience_type: e.target.value, team_id: '', division_id: ''})}
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-light-blue focus:outline-none"
                  data-testid="announcement-audience"
                >
                  <option value="all_users">Everyone (League-Wide)</option>
                  <option value="all_parents">All Parents</option>
                  <option value="all_coaches">All Coaches & Staff</option>
                  <option value="team">Specific Team</option>
                  <option value="division">Specific Division</option>
                  <option value="unpaid_balance">Families with Unpaid Balance</option>
                  <option value="incomplete_registration">Incomplete Registrations</option>
                </select>
              </div>

              {/* Team Selector */}
              {announcementForm.audience_type === 'team' && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Select Team</label>
                  <select
                    value={announcementForm.team_id}
                    onChange={(e) => setAnnouncementForm({...announcementForm, team_id: e.target.value})}
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
                    data-testid="announcement-team"
                  >
                    <option value="">Choose a team...</option>
                    {teams.map(team => (
                      <option key={team.team_id} value={team.team_id}>{team.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Division Selector */}
              {announcementForm.audience_type === 'division' && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Select Division</label>
                  <select
                    value={announcementForm.division_id}
                    onChange={(e) => setAnnouncementForm({...announcementForm, division_id: e.target.value})}
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
                    data-testid="announcement-division"
                  >
                    <option value="">Choose a division...</option>
                    {divisions.map(div => (
                      <option key={div.division_id} value={div.division_id}>{div.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Priority */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Priority</label>
                <div className="flex space-x-2">
                  {['normal', 'important', 'urgent'].map(priority => (
                    <button
                      key={priority}
                      type="button"
                      onClick={() => setAnnouncementForm({...announcementForm, priority})}
                      className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-200 ${
                        announcementForm.priority === priority
                          ? priority === 'urgent' 
                            ? 'bg-red text-white' 
                            : priority === 'important'
                            ? 'bg-gold text-navy'
                            : 'bg-navy text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid={`priority-${priority}`}
                    >
                      {priority}
                    </button>
                  ))}
                </div>
              </div>

              {/* Options */}
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={announcementForm.is_pinned}
                    onChange={(e) => setAnnouncementForm({...announcementForm, is_pinned: e.target.checked})}
                    className="w-4 h-4 text-gold border-gray-300 rounded"
                  />
                  <Pin className="w-4 h-4 text-gold" />
                  <span className="text-sm text-gray-700">Pin to top</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={announcementForm.is_critical}
                    onChange={(e) => setAnnouncementForm({...announcementForm, is_critical: e.target.checked})}
                    className="w-4 h-4 text-red border-gray-300 rounded"
                  />
                  <AlertTriangle className="w-4 h-4 text-red" />
                  <span className="text-sm text-gray-700">Mark as Critical</span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex space-x-3 pt-4 border-t">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAnnouncementModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-red hover:bg-red/90 text-white font-bold uppercase tracking-wider"
                  onClick={handleSendAnnouncement}
                  disabled={submitting || !announcementForm.title.trim() || !announcementForm.body.trim()}
                  data-testid="send-announcement-btn"
                >
                  {submitting ? 'Sending...' : 'Send'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-24 bg-navy/20 rounded-xl"></div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-28 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-testid="admin-dashboard">
        {/* Welcome Banner - Athletic Command Center */}
        <div className="rounded-xl p-6 sm:p-8 mb-6 text-white relative overflow-hidden">
          {/* Field Background */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('/field-bg-1.jpg')` }}
          />
          {/* Light Navy Gradient Overlay (right to left, 5% to 1%) */}
          <div className="absolute inset-0 bg-gradient-to-l from-navy/[0.01] via-navy/[0.03] to-navy/[0.05]" />
          {/* Base navy overlay for text readability */}
          <div className="absolute inset-0 bg-navy/65" />
          <div className="absolute top-0 right-0 w-48 h-48 bg-red/10 rounded-full -translate-y-24 translate-x-24"></div>
          <div className="absolute bottom-0 left-1/3 w-32 h-32 bg-gold/10 rounded-full translate-y-16"></div>
          
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-2">Command Center</p>
              <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-2">
                ADMIN DASHBOARD
              </h1>
              <p className="text-white/60 text-sm font-body">Park City Chiefs League Management</p>
            </div>
            <Button 
              onClick={openAnnouncementModal}
              className="mt-4 sm:mt-0 bg-red hover:bg-red/90 text-white font-bold uppercase tracking-wider"
              data-testid="quick-send-announcement"
            >
              <Megaphone className="w-4 h-4 mr-2" />
              Send Announcement
            </Button>
          </div>
        </div>

        {/* High-Density Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {/* Registrations */}
          <div 
            className="bg-white border border-gray-200 rounded-lg p-4 hover:-translate-y-1 hover:shadow-lg hover:border-navy/30 transition-all duration-200 cursor-pointer"
            onClick={() => navigate('/admin/registrations')}
            data-testid="stat-registrations"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-navy" />
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </div>
            <p className="font-display text-3xl font-black text-navy tracking-tight">{dashboard?.total_registrations || 0}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">Registrations</p>
          </div>

          {/* Incomplete */}
          <div 
            className="bg-white border border-gray-200 rounded-lg p-4 hover:-translate-y-1 hover:shadow-lg hover:border-red/30 transition-all duration-200 cursor-pointer"
            onClick={() => navigate('/admin/registrations')}
            data-testid="stat-incomplete"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-red/10 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red" />
              </div>
              {dashboard?.incomplete_registrations > 0 && (
                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-red text-white">Action</span>
              )}
            </div>
            <p className="font-display text-3xl font-black text-red tracking-tight">{dashboard?.incomplete_registrations || 0}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">Incomplete</p>
          </div>

          {/* Total Players */}
          <div 
            className="bg-white border border-gray-200 rounded-lg p-4 hover:-translate-y-1 hover:shadow-lg hover:border-light-blue/30 transition-all duration-200 cursor-pointer"
            onClick={() => navigate('/admin/teams')}
            data-testid="stat-players"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-light-blue/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-light-blue" />
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </div>
            <p className="font-display text-3xl font-black text-light-blue tracking-tight">{dashboard?.total_players || 0}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">Players</p>
          </div>

          {/* Revenue */}
          <div 
            className="bg-white border border-gray-200 rounded-lg p-4 hover:-translate-y-1 hover:shadow-lg hover:border-green-300 transition-all duration-200"
            data-testid="stat-revenue"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <p className="font-display text-3xl font-black text-green-600 tracking-tight">${(dashboard?.total_revenue || 0).toLocaleString()}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">Revenue</p>
          </div>
        </div>

        {/* Secondary Stats Row */}
        {(rosterStats || scheduleStats) && (
          <div className="grid grid-cols-4 gap-3 mb-6">
            <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <p className="font-display text-2xl font-black text-green-600">{rosterStats?.cleared_players || 0}</p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Cleared</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <p className="font-display text-2xl font-black text-gold">{rosterStats?.pending_eligibility || 0}</p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Pending</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <p className="font-display text-2xl font-black text-light-blue">{rosterStats?.waitlisted_players || 0}</p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Waitlist</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <p className="font-display text-2xl font-black text-navy">{scheduleStats?.total_upcoming || 0}</p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Events</p>
            </div>
          </div>
        )}

        {/* Main Content Grid - High Density */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Financial Overview */}
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="financial-card">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
              <h2 className="font-display text-xl font-black text-navy uppercase tracking-tight">Financial Overview</h2>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 border border-green-100 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-green-700">Collected</span>
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <p className="font-display text-2xl font-black text-green-600">${dashboard?.total_revenue?.toFixed(0) || '0'}</p>
                </div>

                <div className="p-4 bg-red/5 border border-red/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-red">Outstanding</span>
                    <AlertCircle className="w-5 h-5 text-red" />
                  </div>
                  <p className="font-display text-2xl font-black text-red">${dashboard?.unpaid_balance?.toFixed(0) || '0'}</p>
                </div>

                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-600">Pending Verify</span>
                    <FileCheck className="w-5 h-5 text-navy" />
                  </div>
                  <p className="font-display text-2xl font-black text-navy">{dashboard?.pending_verification || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="quick-actions-card">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
              <h2 className="font-display text-xl font-black text-navy uppercase tracking-tight">Quick Actions</h2>
            </div>
            <div className="p-4 space-y-2">
              <button 
                onClick={() => navigate('/admin/registrations')}
                className="w-full p-3 bg-navy text-white rounded-lg hover:bg-navy/90 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 text-xs font-bold uppercase tracking-wider flex items-center justify-between"
              >
                <span>View Registrations</span>
                <ChevronRight className="w-4 h-4" />
              </button>
              <button 
                onClick={() => navigate('/admin/teams')}
                className="w-full p-3 bg-light-blue text-white rounded-lg hover:bg-light-blue/90 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 text-xs font-bold uppercase tracking-wider flex items-center justify-between"
              >
                <span>Manage Teams</span>
                <ChevronRight className="w-4 h-4" />
              </button>
              <button 
                onClick={() => navigate('/admin/coach-applications')}
                className="w-full p-3 bg-gold text-navy rounded-lg hover:bg-gold/90 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 text-xs font-bold uppercase tracking-wider flex items-center justify-between"
              >
                <span>Coach Applications</span>
                <ChevronRight className="w-4 h-4" />
              </button>
              <button 
                onClick={() => navigate('/admin/accountability')}
                className="w-full p-3 bg-red text-white rounded-lg hover:bg-red/90 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 text-xs font-bold uppercase tracking-wider flex items-center justify-between"
                data-testid="quick-action-accountability"
              >
                <span>Accountability</span>
                <ChevronRight className="w-4 h-4" />
              </button>
              <button 
                onClick={() => navigate('/schedule')}
                className="w-full p-3 border-2 border-gray-200 text-navy rounded-lg hover:border-navy hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 text-xs font-bold uppercase tracking-wider flex items-center justify-between"
              >
                <span>View Schedule</span>
                <ChevronRight className="w-4 h-4" />
              </button>
              {/* Manage Public Page — collapses the four public-page
                  destinations (Gallery, Spotlights, Sponsors, Registration
                  Config) into one accordion. Routes are unchanged; only
                  the Quick Actions card is shortened. */}
              <div>
                <button
                  onClick={() => setShowPublicPageMenu((v) => !v)}
                  className="w-full p-3 border-2 border-gray-200 text-navy rounded-lg hover:border-navy hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 text-xs font-bold uppercase tracking-wider flex items-center justify-between"
                  aria-expanded={showPublicPageMenu}
                  data-testid="quick-action-public-page"
                >
                  <span className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Manage Public Page
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showPublicPageMenu ? 'rotate-180' : ''}`} />
                </button>
                {showPublicPageMenu && (
                  <div className="pl-4 mt-2 space-y-2" data-testid="public-page-submenu">
                    <button
                      onClick={() => navigate('/admin/gallery')}
                      className="w-full p-2.5 bg-gray-50 border border-gray-200 text-navy rounded-lg hover:bg-white hover:border-navy transition-all duration-200 text-xs font-bold uppercase tracking-wider flex items-center justify-between"
                      data-testid="quick-action-gallery"
                    >
                      <span>Manage Gallery</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => navigate('/admin/spotlights')}
                      className="w-full p-2.5 bg-gray-50 border border-gray-200 text-navy rounded-lg hover:bg-white hover:border-navy transition-all duration-200 text-xs font-bold uppercase tracking-wider flex items-center justify-between"
                      data-testid="quick-action-spotlights"
                    >
                      <span>Player Spotlights</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => navigate('/admin/sponsors')}
                      className="w-full p-2.5 bg-gray-50 border border-gray-200 text-navy rounded-lg hover:bg-white hover:border-navy transition-all duration-200 text-xs font-bold uppercase tracking-wider flex items-center justify-between"
                      data-testid="quick-action-sponsors"
                    >
                      <span>Sponsors</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => navigate('/admin/program-config')}
                      className="w-full p-2.5 bg-gray-50 border border-gray-200 text-navy rounded-lg hover:bg-white hover:border-navy transition-all duration-200 text-xs font-bold uppercase tracking-wider flex items-center justify-between"
                      data-testid="quick-action-program-config"
                    >
                      <span>Registration Config</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={() => navigate('/admin/roster-notes')}
                className="w-full p-3 border-2 border-gray-200 text-navy rounded-lg hover:border-navy hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 text-xs font-bold uppercase tracking-wider flex items-center justify-between"
                data-testid="quick-action-roster-notes"
              >
                <span>Roster Notes</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6" data-testid="events-card">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
            <h2 className="font-display text-xl font-black text-navy uppercase tracking-tight">Upcoming Events</h2>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/schedule')}
              className="text-navy hover:text-red hover:bg-transparent font-semibold text-sm"
            >
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="p-5">
            {dashboard?.events && dashboard.events.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {dashboard.events.slice(0, 6).map((event) => (
                  <div
                    key={event.event_id}
                    className="flex items-center space-x-3 p-3 bg-gray-50 border border-gray-100 rounded-lg hover:border-navy/20 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
                    onClick={() => navigate(`/events/${event.event_id}`)}
                    data-testid={`event-${event.event_id}`}
                  >
                    <div className="w-12 h-12 bg-navy/10 rounded-lg flex flex-col items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-bold text-gray-500 uppercase">
                        {new Date(event.start_time).toLocaleDateString('en-US', { month: 'short' })}
                      </span>
                      <span className="font-display text-lg font-black text-navy">
                        {new Date(event.start_time).getDate()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-navy text-sm truncate">{event.title}</p>
                      <p className="text-xs text-gray-500 truncate">{event.location}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No upcoming events</p>
              </div>
            )}
          </div>
        </div>

        {/* Admin Tasks Widget */}
        <div className="mb-6">
          <AdminTasksWidget />
        </div>

        {/* Community & Volunteer Management */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Community Moderation */}
          <div 
            className={`bg-white border rounded-xl overflow-hidden ${communityStats?.pending_moderation > 0 ? 'border-gold' : 'border-gray-200'}`}
            data-testid="community-card"
          >
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
              <div className="flex items-center">
                <MessageSquare className="w-5 h-5 text-navy mr-2" />
                <h2 className="font-display text-lg font-black text-navy uppercase tracking-tight">Community</h2>
              </div>
              {communityStats?.pending_moderation > 0 && (
                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-gold text-navy">
                  {communityStats.pending_moderation} Pending
                </span>
              )}
            </div>
            <div className="p-5">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <p className="font-display text-2xl font-black text-navy">{communityStats?.total_posts || 0}</p>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Posts</p>
                </div>
                <div className="text-center">
                  <p className="font-display text-2xl font-black text-gold">{communityStats?.pending_moderation || 0}</p>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Pending</p>
                </div>
                <div className="text-center">
                  <p className="font-display text-2xl font-black text-green-600">{communityStats?.recent_activity || 0}</p>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">This Week</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/admin/community')}
                className={`w-full py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-200 ${
                  communityStats?.pending_moderation > 0 
                    ? 'bg-gold text-navy hover:bg-gold/90' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid="review-posts-btn"
              >
                {communityStats?.pending_moderation > 0 ? 'Review Posts' : 'Manage Community'}
              </button>
            </div>
          </div>

          {/* Volunteer Management */}
          <div 
            className={`bg-white border rounded-xl overflow-hidden ${volunteerStats?.unfilled_slots > 10 ? 'border-red' : 'border-gray-200'}`}
            data-testid="volunteer-card"
          >
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
              <div className="flex items-center">
                <HandHeart className="w-5 h-5 text-navy mr-2" />
                <h2 className="font-display text-lg font-black text-navy uppercase tracking-tight">Volunteers</h2>
              </div>
              {volunteerStats?.unfilled_slots > 10 && (
                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-red text-white">
                  Need Help
                </span>
              )}
            </div>
            <div className="p-5">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <p className="font-display text-2xl font-black text-navy">{volunteerStats?.open_opportunities || 0}</p>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Open</p>
                </div>
                <div className="text-center">
                  <p className="font-display text-2xl font-black text-red">{volunteerStats?.unfilled_slots || 0}</p>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Unfilled</p>
                </div>
                <div className="text-center">
                  <p className="font-display text-2xl font-black text-green-600">{volunteerStats?.total_signups || 0}</p>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Signups</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/admin/volunteers')}
                className="w-full py-2.5 bg-green-100 text-green-700 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-green-200 transition-all duration-200"
                data-testid="manage-volunteers-btn"
              >
                Manage Volunteers
              </button>
            </div>
          </div>
        </div>

        {/* Announcement Modal (inlined render — NOT a nested component, see comment above) */}
        {renderAnnouncementModal()}
      </div>
    </ParentLayout>
  );
};

export default AdminDashboard;